import type { App } from 'vue';
import type { Router } from 'vue-router';
import axios from 'axios';
import { apiClient, getAccessToken } from '@/api/client';
import { isAxiosRejection, isBenignBrowserError, isPossibleRestart } from './error-filters';

/**
 * Manda al monitor de Desarrollo los errores de esta pagina: excepciones de
 * JavaScript, promesas sin manejar, errores de Vue y respuestas 5xx del API.
 * En lotes cada 5 s, sin repetir el mismo error mas de 3 veces por minuto, y
 * con los ultimos pasos (paginas, llamadas fallidas) para reproducirlo.
 */
interface WebError {
  level: 'FATAL' | 'ERROR' | 'WARNING';
  type: string;
  message: string;
  stackTrace?: string;
  handled: boolean;
  occurredAt: number;
  screen: string;
  breadcrumbs: { at: number; category: string; message: string }[];
  context: Record<string, unknown>;
}

const breadcrumbs: WebError['breadcrumbs'] = [];
const queue: WebError[] = [];
const seen = new Map<string, { minute: number; count: number }>();
let timer: ReturnType<typeof setInterval> | undefined;

export function addBreadcrumb(category: string, message: string) {
  breadcrumbs.push({ at: Date.now(), category, message: message.slice(0, 300) });
  if (breadcrumbs.length > 30) breadcrumbs.shift();
}

export function captureError(error: unknown, extra: Partial<WebError> = {}) {
  if (isBenignBrowserError(error)) return;
  const err = error instanceof Error ? error : new Error(typeof error === 'string' ? error : JSON.stringify(error));
  const type = extra.type ?? err.name ?? 'Error';
  const key = `${type}|${err.message}`;
  const minute = Math.floor(Date.now() / 60_000);
  const s = seen.get(key);
  if (s && s.minute === minute && s.count >= 3) return;
  seen.set(key, s && s.minute === minute ? { minute, count: s.count + 1 } : { minute, count: 1 });
  queue.push({
    level: extra.level ?? 'ERROR',
    type: type.slice(0, 200),
    message: (err.message || type).slice(0, 2000),
    stackTrace: err.stack?.slice(0, 20000),
    handled: extra.handled ?? false,
    occurredAt: Date.now(),
    screen: window.location.pathname,
    breadcrumbs: [...breadcrumbs],
    context: {
      host: window.location.host,
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      online: navigator.onLine,
      ...(extra.context ?? {}),
    },
  });
}

async function flush() {
  if (!queue.length || !getAccessToken()) return;
  const events = queue.splice(0, 20);
  try {
    await apiClient.post('/monitor/web-errors', { events }, { headers: { 'x-skip-capture': '1' } });
  } catch {
    // Sin conexion: se reintenta en la proxima vuelta (sin crecer sin limite).
    if (queue.length < 100) queue.unshift(...events);
  }
}

export function installErrorCapture(app: App, router: Router) {
  window.addEventListener('error', (e) => {
    if (e.error || e.message) captureError(e.error ?? new Error(e.message), { type: e.error?.name ?? 'WindowError', context: { file: e.filename, line: e.lineno, column: e.colno } });
  });
  window.addEventListener('unhandledrejection', (e) => {
    // Una llamada al API que fallo ya la reporto el interceptor (5xx / sin red) o es
    // un 4xx esperado: repetirla como "UnhandledRejection" solo duplicaba el error.
    if (isAxiosRejection(e.reason)) return;
    captureError(e.reason, { type: 'UnhandledRejection' });
  });
  const previous = app.config.errorHandler;
  app.config.errorHandler = (err, instance, info) => {
    captureError(err, { type: (err as Error)?.name ?? 'VueError', context: { component: instance?.$options?.name ?? instance?.$options?.__name, info } });
    previous?.(err, instance, info);
    // Sigue apareciendo en la consola del navegador para quien este depurando.
    console.error(err);
  };
  router.afterEach((to) => addBreadcrumb('navegación', to.fullPath));
  apiClient.interceptors.response.use(
    (r) => r,
    (error) => {
      if (axios.isAxiosError(error) && !error.config?.headers?.['x-skip-capture']) {
        const status = error.response?.status;
        const where = `${error.config?.method?.toUpperCase()} ${error.config?.url}`;
        addBreadcrumb('api', `${where} → ${status ?? 'sin respuesta'}`);
        if (error.code === 'ERR_CANCELED') return Promise.reject(error);
        if (status && status >= 500 && !isPossibleRestart(status)) {
          captureError(new Error(`${where} respondió ${status}`), {
            type: `HTTP_${status}`,
            handled: true,
            context: { correlationId: error.response?.data?.correlationId, response: JSON.stringify(error.response?.data ?? '').slice(0, 1000) },
          });
        } else if (isPossibleRestart(status)) {
          reportIfOutagePersists(
            status
              ? new Error(`${where} respondió ${status}`)
              : new Error(`${where}: el servidor no respondió (${error.code ?? 'red'})`),
            status ? { type: `HTTP_${status}`, handled: true } : { type: 'NETWORK_ERROR', level: 'WARNING', handled: true },
          );
        }
      }
      return Promise.reject(error);
    },
  );
  timer = setInterval(flush, 5000);
  window.addEventListener('pagehide', () => void flush());
}

// ---------------------------------------------------------------- Reinicios del servidor

/** Cuanto se espera antes de decidir que no fue solo un despliegue (el API tarda ~30 s en arrancar). */
const OUTAGE_GRACE_MS = 60_000;
const outageCandidates: { error: Error; extra: Partial<WebError> }[] = [];
let outageTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * 502/503/504 o sin respuesta: casi siempre es el servidor reiniciandose en un
 * despliegue. Se espera un minuto: si el API ya responde, queda solo como paso
 * ("el servidor no respondió y se recuperó"); si sigue caido, se reporta.
 */
function reportIfOutagePersists(error: Error, extra: Partial<WebError>) {
  if (outageCandidates.length < 20) outageCandidates.push({ error, extra });
  if (outageTimer) return;
  outageTimer = setTimeout(async () => {
    outageTimer = undefined;
    const items = outageCandidates.splice(0);
    const recovered = await apiClient
      .get('/health', { headers: { 'x-skip-capture': '1' }, timeout: 8000 })
      .then(() => true)
      .catch(() => false);
    if (recovered) {
      addBreadcrumb('api', `El servidor no respondió un momento (${items.length} llamadas) y se recuperó`);
      return;
    }
    for (const { error: e, extra: x } of items)
      captureError(e, { ...x, context: { ...(x.context ?? {}), persistedMs: OUTAGE_GRACE_MS } });
  }, OUTAGE_GRACE_MS);
}

export function stopErrorCapture() {
  if (timer) clearInterval(timer);
}
